import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Buildings, Plus, X, Trash, CheckCircle, Certificate, Users,
  FileText, CaretRight, Bank, Shield, Warning, Tag, User,
  Envelope, Phone, CalendarBlank, Link as LinkIcon, PencilSimple,
  Upload, CloudArrowUp, IdentificationCard, Briefcase, Globe,
  Scales, ShieldCheck, Database, Robot, Calendar, GitBranch,
  UserCircle, Download, Eye, ArrowRight, Info, ChartPie, Clock,
  MapPin, Percent, Lightning, TreeStructure
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Reference data labels
const REGULATOR_LABELS = {
  fsca: "Financial Sector Conduct Authority (FSCA)",
  pa: "Prudential Authority (PA)",
  info_reg: "Information Regulator (IR)",
  cipc: "CIPC",
  jse: "JSE",
  sarb: "SARB",
  sars: "SARS (FATCA/CRS)",
  ncr: "National Credit Regulator (NCR)",
  cms: "Council for Medical Schemes",
  fsra: "FSRA",
  fic: "Financial Intelligence Centre (FIC)",
};

const ACTIVITY_LABELS = {
  legal_practitioner: "Legal Practitioner",
  trust_company_service: "Trust & Company Service Provider",
  estate_property: "Estate/Property Practitioner",
  exchange_user: "Authorised User of an Exchange",
  cis_manager: "Collective Investment Scheme Manager",
  bank: "Bank",
  mutual_bank: "Mutual Bank",
  cooperative_bank: "Co-operative Bank",
  long_term_insurer: "Long-term/Life Insurer",
  forex_business: "Foreign Exchange Business",
  credit_provider: "Credit Provider",
  fsp: "Financial Services Provider",
  money_transfer: "Money/Value Transfer Provider",
  high_value_goods: "High-Value Goods Dealer",
  gambling: "Gambling Institution",
  casp: "Crypto Asset Service Provider",
};

const BUSINESS_ACTIVITY_LABELS = {
  financial_advice: "Financial Advice",
  intermediary_services: "Intermediary Services",
  discretionary_investment: "Discretionary Investment Management",
  fund_administration: "Fund Administration",
  insurance_underwriting: "Insurance Underwriting",
  claims_administration: "Claims Administration",
  deposit_taking: "Deposit Taking",
  payments: "Payments",
  money_transfer: "Money/Value Transfer",
  crypto_services: "Crypto-Asset Services",
  credit: "Credit",
  asset_custody: "Asset Custody",
  cis_management: "Collective Investment Management",
  retirement_admin: "Retirement Fund Administration",
};

const FSP_CATEGORY_LABELS = {
  cat_i: "Category I - Advice & Intermediary Services",
  cat_ii: "Category II - Discretionary FSP",
  cat_iia: "Category IIA - Hedge Fund FSP",
  cat_iii: "Category III - Administrative FSP",
  cat_iv: "Category IV - Assistance Business FSP",
};

const DATA_PRIVACY_LABELS = {
  controller: "Responsible Party (Data Controller)",
  processor: "Operator (Data Processor)",
  neither: "Neither",
};

// Info Card Component
const InfoCard = ({ icon: Icon, label, value, emptyText = "Not specified" }) => (
  <div className="flex items-start gap-3 p-4 bg-background-surface border border-border rounded-lg">
    <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon size={20} className="text-gold" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs uppercase tracking-wider text-text-tertiary mb-1">{label}</div>
      <div className="text-sm text-text-primary font-medium truncate">
        {value || <span className="text-text-tertiary italic">{emptyText}</span>}
      </div>
    </div>
  </div>
);

// Officer Card Component
const OfficerCard = ({ officer, type }) => (
  <div className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg">
    <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center">
      <UserCircle size={20} className="text-gold" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-text-primary">
        {officer.first_name || officer.name} {officer.surname || officer.last_name}
      </div>
      <div className="text-xs text-text-tertiary flex items-center gap-1">
        <Envelope size={12} />
        {officer.email}
      </div>
      {officer.re5_number && (
        <div className="text-xs text-gold">RE5: {officer.re5_number}</div>
      )}
    </div>
  </div>
);

// Beneficial Owner Card
const BeneficialOwnerCard = ({ owner }) => (
  <div className="p-4 bg-background border border-border rounded-lg">
    <div className="flex items-start justify-between mb-2">
      <div>
        <div className="text-sm font-medium text-text-primary">{owner.name}</div>
        <div className="text-xs text-text-tertiary capitalize">{owner.person_or_entity}</div>
      </div>
      {owner.navi_extracted && (
        <span className="text-xs px-2 py-0.5 bg-cyan-500/10 text-cyan-400 rounded">NAVI</span>
      )}
    </div>
    <div className="grid grid-cols-2 gap-2 text-xs">
      {owner.ownership_percentage && (
        <div className="flex items-center gap-1">
          <Percent size={12} className="text-gold" />
          <span className="text-text-secondary">{owner.ownership_percentage}% Ownership</span>
        </div>
      )}
      {owner.voting_percentage && (
        <div className="flex items-center gap-1">
          <ChartPie size={12} className="text-gold" />
          <span className="text-text-secondary">{owner.voting_percentage}% Voting</span>
        </div>
      )}
      {owner.country && (
        <div className="flex items-center gap-1">
          <Globe size={12} className="text-gold" />
          <span className="text-text-secondary">{owner.country}</span>
        </div>
      )}
    </div>
  </div>
);

// Director Card
const DirectorCard = ({ director, type }) => (
  <div className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg">
    <div className={`w-10 h-10 ${type === 'executive' ? 'bg-emerald-500/10' : 'bg-blue-500/10'} rounded-full flex items-center justify-center`}>
      <User size={20} className={type === 'executive' ? 'text-emerald-400' : 'text-blue-400'} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-text-primary">
        {director.first_name} {director.surname}
      </div>
      <div className="text-xs text-text-tertiary">
        {director.nationality && `${director.nationality} · `}
        {director.date_of_appointment && `Appointed: ${new Date(director.date_of_appointment).toLocaleDateString()}`}
      </div>
      <div className="flex gap-2 mt-1">
        {director.is_pep && <span className="text-xs px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded">PEP</span>}
        {director.is_pip && <span className="text-xs px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded">PIP</span>}
      </div>
    </div>
  </div>
);

// Section Header Component
const SectionHeader = ({ icon: Icon, title, action }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon size={20} className="text-gold" />
      <h3 className="text-lg font-heading font-medium text-text-primary">{title}</h3>
    </div>
    {action}
  </div>
);

// Progress Ring Component
const ProgressRing = ({ percentage, size = 80, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-border"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-gold transition-all duration-500"
      />
    </svg>
  );
};

// Main Page Component
const CompanyOverviewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Methodology state
  const [methodologies, setMethodologies] = useState([]);
  const [methodologyLoading, setMethodologyLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedMethodology, setSelectedMethodology] = useState(null);
  const [auraDefault, setAuraDefault] = useState(null);
  const [usingMethodology, setUsingMethodology] = useState('aura_default');

  useEffect(() => {
    fetchOverviewData();
  }, []);

  useEffect(() => {
    if (activeTab === 'methodology') {
      fetchMethodologies();
      fetchAuraDefault();
    }
  }, [activeTab]);

  const fetchMethodologies = async () => {
    setMethodologyLoading(true);
    try {
      const response = await axios.get(`${API}/company-profile/methodologies`);
      setMethodologies(response.data.methodologies || []);
      setUsingMethodology(response.data.using_methodology || 'aura_default');
    } catch (error) {
      console.error('Failed to load methodologies:', error);
    } finally {
      setMethodologyLoading(false);
    }
  };

  const fetchAuraDefault = async () => {
    try {
      const response = await axios.get(`${API}/company-profile/aura-default-methodology`);
      setAuraDefault(response.data);
    } catch (error) {
      console.error('Failed to load AURA default:', error);
    }
  };

  const fetchOverviewData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/intake-wizard/v2/company-overview`);
      setOverviewData(response.data);
    } catch (error) {
      console.error('Failed to load company overview:', error);
      // Fallback to null - will show intake prompt
      setOverviewData({ source: 'none', redirect_to_intake: true });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="text-gold font-mono">Loading company overview...</div>
        </div>
      </div>
    );
  }

  // Check if we need to redirect to intake
  const needsIntake = !overviewData || overviewData.redirect_to_intake && !overviewData.intake_completed;

  const tabs = [
    { id: 'overview', label: 'Company Overview', icon: Buildings },
    { id: 'compliance', label: 'Compliance & Regulators', icon: Shield },
    { id: 'business', label: 'Business Profile', icon: Briefcase },
    { id: 'data', label: 'Data & Privacy', icon: Database },
    { id: 'ownership', label: 'Beneficial Ownership', icon: TreeStructure },
    { id: 'methodology', label: 'Risk Methodology', icon: ChartPie },
  ];

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary mb-1" data-testid="company-overview-title">
                Company Overview
              </h1>
              <p className="text-sm text-text-secondary">
                {overviewData?.has_v2_intake 
                  ? 'Your company profile based on the intake questionnaire'
                  : 'Complete the intake wizard to set up your company profile'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {overviewData?.registered_company_name && (
                <div className="px-3 py-1.5 bg-gold/10 border border-gold rounded-sm">
                  <span className="text-sm font-medium text-gold">
                    {overviewData.registered_company_name}
                  </span>
                </div>
              )}
              {overviewData?.can_edit_intake && (
                <button
                  onClick={() => navigate('/intake')}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all text-sm flex items-center gap-2"
                  data-testid="edit-intake-btn"
                >
                  <PencilSimple size={16} />
                  {overviewData?.intake_completed ? 'Edit Intake' : 'Complete Intake'}
                </button>
              )}
            </div>
          </div>

          {/* Progress Banner for incomplete intake */}
          {overviewData?.has_v2_intake && !overviewData?.intake_completed && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-gold/10 border border-gold/30 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <ProgressRing percentage={overviewData.progress_percentage || 0} size={60} strokeWidth={6} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-medium text-gold">{overviewData.progress_percentage || 0}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">Complete Your Company Profile</p>
                    <p className="text-sm text-text-secondary">
                      {overviewData.completed_steps?.length || 0} of 6 steps completed
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/intake')}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all text-sm flex items-center gap-2"
                  data-testid="continue-intake-btn"
                >
                  Continue
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {/* No intake data - prompt to start */}
          {needsIntake && !overviewData?.has_v2_intake && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-6 bg-gold/5 border border-gold/20 rounded-lg text-center"
            >
              <Warning size={48} className="text-gold mx-auto mb-4" weight="duotone" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Company Profile Not Set Up
              </h3>
              <p className="text-text-secondary mb-4 max-w-md mx-auto">
                Complete the Intake Wizard to configure your regulatory profile, add compliance officers, and define your business activities.
              </p>
              <button
                onClick={() => navigate('/intake')}
                className="px-6 py-3 bg-gold text-gold-foreground rounded-lg hover:bg-gold-hover transition-all text-sm font-medium flex items-center gap-2 mx-auto"
                data-testid="start-intake-btn"
              >
                <PencilSimple size={18} />
                Start Intake Wizard
              </button>
            </motion.div>
          )}

          {/* Quick Stats */}
          {overviewData?.has_v2_intake && (
            <div className="grid grid-cols-5 gap-4 mt-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-background-surface border border-border rounded-sm"
              >
                <div className="text-2xl font-mono font-semibold text-gold">
                  {(overviewData.fais_compliance_officers?.length || 0) + 
                   (overviewData.mlros?.length || 0)}
                </div>
                <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                  Compliance Officers
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-4 bg-background-surface border border-border rounded-sm"
              >
                <div className="text-2xl font-mono font-semibold text-gold">
                  {overviewData.primary_regulators?.length || 0}
                </div>
                <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                  Regulators
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-4 bg-background-surface border border-border rounded-sm"
              >
                <div className="text-2xl font-mono font-semibold text-gold">
                  {(overviewData.aura_activity_categories?.length || 0) + 
                   (overviewData.business_activities?.length || 0)}
                </div>
                <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                  Business Activities
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-4 bg-background-surface border border-border rounded-sm"
              >
                <div className="text-2xl font-mono font-semibold text-gold">
                  {(overviewData.beneficial_owners?.length || 0) + 
                   (overviewData.executive_directors?.length || 0) +
                   (overviewData.non_executive_directors?.length || 0)}
                </div>
                <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                  Directors/Owners
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="p-4 bg-background-surface border border-border rounded-sm"
              >
                <div className="text-2xl font-mono font-semibold text-gold">
                  {overviewData.universe_regulation_count || 0}
                </div>
                <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                  Regulations
                </div>
              </motion.div>
            </div>
          )}
        </div>

        {/* Section Tabs */}
        {overviewData?.has_v2_intake && (
          <div className="px-6 pt-6">
            <div className="flex gap-1 border-b border-border">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2.5 text-sm font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id 
                      ? 'text-gold border-b-2 border-gold' 
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Company Overview Tab */}
          {overviewData?.has_v2_intake && activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Company Registration */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Buildings} title="Company Registration" />
                <div className="grid grid-cols-2 gap-4">
                  <InfoCard 
                    icon={Buildings} 
                    label="Registered Company Name" 
                    value={overviewData.registered_company_name}
                  />
                  <InfoCard 
                    icon={IdentificationCard} 
                    label="Registration Number" 
                    value={overviewData.registration_number}
                  />
                  <InfoCard 
                    icon={Globe} 
                    label="Country of Incorporation" 
                    value={overviewData.country_of_incorporation}
                  />
                  <InfoCard 
                    icon={Calendar} 
                    label="Financial Year End" 
                    value={overviewData.financial_year_end}
                  />
                </div>
              </div>

              {/* Parent Company */}
              {overviewData.parent_company_name && (
                <div className="bg-background-surface border border-border rounded-lg p-6">
                  <SectionHeader icon={GitBranch} title="Parent Company" />
                  <div className="grid grid-cols-2 gap-4">
                    <InfoCard 
                      icon={Buildings} 
                      label="Parent Company Name" 
                      value={overviewData.parent_company_name}
                    />
                    <InfoCard 
                      icon={IdentificationCard} 
                      label="Parent Registration Number" 
                      value={overviewData.parent_company_registration}
                    />
                  </div>
                  {overviewData.comply_with_parent_regulations && (
                    <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={18} className="text-emerald-400" weight="fill" />
                        <span className="text-sm text-emerald-400">Subject to parent company regulations</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Intake Status */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={CheckCircle} title="Intake Status" />
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-background border border-border rounded-lg text-center">
                    <div className={`text-lg font-semibold ${overviewData.intake_completed ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {overviewData.intake_completed ? 'Complete' : 'In Progress'}
                    </div>
                    <div className="text-xs text-text-tertiary mt-1">Status</div>
                  </div>
                  <div className="p-4 bg-background border border-border rounded-lg text-center">
                    <div className="text-lg font-mono font-semibold text-gold">
                      {overviewData.completed_steps?.length || 0}/6
                    </div>
                    <div className="text-xs text-text-tertiary mt-1">Steps Completed</div>
                  </div>
                  <div className="p-4 bg-background border border-border rounded-lg text-center">
                    <div className="text-lg font-semibold text-text-primary">
                      {overviewData.submitted_at 
                        ? new Date(overviewData.submitted_at).toLocaleDateString() 
                        : 'Not submitted'}
                    </div>
                    <div className="text-xs text-text-tertiary mt-1">Submitted Date</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Compliance & Regulators Tab */}
          {overviewData?.has_v2_intake && activeTab === 'compliance' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Compliance Function */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Shield} title="Compliance Function" />
                <div className="flex flex-wrap gap-2">
                  {overviewData.compliance_function_internal && (
                    <span className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-emerald-400">
                      Internal Compliance
                    </span>
                  )}
                  {overviewData.compliance_function_external && (
                    <span className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-400">
                      Outsourced Compliance
                    </span>
                  )}
                  {overviewData.compliance_function_cosourced && (
                    <span className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg text-sm text-purple-400">
                      Co-sourced Compliance
                    </span>
                  )}
                </div>
                {overviewData.external_compliance_entity_name && (
                  <div className="mt-4">
                    <InfoCard 
                      icon={Briefcase} 
                      label="External Compliance Provider" 
                      value={overviewData.external_compliance_entity_name}
                    />
                  </div>
                )}
              </div>

              {/* Regulators */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Scales} title="Primary Regulators" />
                {overviewData.primary_regulators?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {overviewData.primary_regulators.map((reg, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1.5 bg-gold/10 border border-gold/30 rounded-lg text-sm text-gold"
                      >
                        {REGULATOR_LABELS[reg] || reg}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No regulators specified</p>
                )}
              </div>

              {/* FSP Details */}
              {(overviewData.fsp_number || overviewData.fsp_categories?.length > 0) && (
                <div className="bg-background-surface border border-border rounded-lg p-6">
                  <SectionHeader icon={Certificate} title="FSP Registration" />
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <InfoCard icon={Certificate} label="FSP Number" value={overviewData.fsp_number} />
                    <InfoCard icon={Calendar} label="Registration Date" value={overviewData.fsp_registration_date} />
                  </div>
                  {overviewData.fsp_categories?.length > 0 && (
                    <div>
                      <label className="text-xs text-text-tertiary uppercase tracking-wider mb-2 block">FSP Categories</label>
                      <div className="flex flex-wrap gap-2">
                        {overviewData.fsp_categories.map((cat, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-cyan-500/10 text-cyan-400 text-sm rounded-lg">
                            {FSP_CATEGORY_LABELS[cat] || cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* FIC Registration */}
              {(overviewData.fic_org_id || overviewData.fic_schedules?.length > 0) && (
                <div className="bg-background-surface border border-border rounded-lg p-6">
                  <SectionHeader icon={ShieldCheck} title="FIC Registration" />
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <InfoCard icon={IdentificationCard} label="FIC Org ID" value={overviewData.fic_org_id} />
                    <InfoCard icon={Calendar} label="Registration Date" value={overviewData.fic_registration_date} />
                  </div>
                  {overviewData.fic_schedules?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {overviewData.fic_schedules.map((sched, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-amber-500/10 text-amber-400 text-sm rounded-lg">
                          {sched}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* FAIS Compliance Officers */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Shield} title={`FAIS Compliance Officers (${overviewData.fais_compliance_officers?.length || 0})`} />
                {overviewData.fais_compliance_officers?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {overviewData.fais_compliance_officers.map((officer, idx) => (
                      <OfficerCard key={idx} officer={officer} type="FAIS" />
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No FAIS Compliance Officers specified</p>
                )}
              </div>

              {/* Key Individuals */}
              {overviewData.key_individuals?.length > 0 && (
                <div className="bg-background-surface border border-border rounded-lg p-6">
                  <SectionHeader icon={Users} title={`Key Individuals (${overviewData.key_individuals.length})`} />
                  <div className="grid grid-cols-2 gap-3">
                    {overviewData.key_individuals.map((ki, idx) => (
                      <OfficerCard key={idx} officer={ki} type="KI" />
                    ))}
                  </div>
                </div>
              )}

              {/* MLROs */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={ShieldCheck} title={`MLROs (${overviewData.mlros?.length || 0})`} />
                {overviewData.mlros?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {overviewData.mlros.map((officer, idx) => (
                      <OfficerCard key={idx} officer={officer} type="MLRO" />
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No MLROs specified</p>
                )}
              </div>
            </motion.div>
          )}

          {/* Business Profile Tab */}
          {overviewData?.has_v2_intake && activeTab === 'business' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Industries */}
              {overviewData.industries?.length > 0 && (
                <div className="bg-background-surface border border-border rounded-lg p-6">
                  <SectionHeader icon={Bank} title="Industries" />
                  <div className="flex flex-wrap gap-2">
                    {overviewData.industries.map((ind, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-gold/10 border border-gold/30 rounded-lg text-sm text-gold">
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* AURA Activity Categories */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Lightning} title="AURA Activity Categories (Schedule 1)" />
                {overviewData.aura_activity_categories?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {overviewData.aura_activity_categories.map((act, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-emerald-400">
                        {ACTIVITY_LABELS[act] || act}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No AURA categories selected</p>
                )}
              </div>

              {/* Business Activities */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Briefcase} title="Business Activities" />
                {overviewData.business_activities?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {overviewData.business_activities.map((act, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-400">
                        {BUSINESS_ACTIVITY_LABELS[act] || act}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No business activities selected</p>
                )}
                
                {overviewData.other_activity_description && (
                  <div className="mt-4 p-3 bg-background border border-border rounded-lg">
                    <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Other Activities</div>
                    <p className="text-sm text-text-primary">{overviewData.other_activity_description}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Data & Privacy Tab */}
          {overviewData?.has_v2_intake && activeTab === 'data' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Data Privacy Role */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Database} title="POPIA Data Privacy Role" />
                <div className="p-4 bg-background border border-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      overviewData.data_privacy_role === 'controller' ? 'bg-emerald-500/10' :
                      overviewData.data_privacy_role === 'processor' ? 'bg-blue-500/10' :
                      'bg-gray-500/10'
                    }`}>
                      <Database size={24} className={
                        overviewData.data_privacy_role === 'controller' ? 'text-emerald-400' :
                        overviewData.data_privacy_role === 'processor' ? 'text-blue-400' :
                        'text-gray-400'
                      } />
                    </div>
                    <div>
                      <div className="text-lg font-medium text-text-primary">
                        {DATA_PRIVACY_LABELS[overviewData.data_privacy_role] || 'Not specified'}
                      </div>
                      <div className="text-sm text-text-tertiary">
                        {overviewData.data_privacy_role === 'controller' && 'You determine the purpose and means of processing personal information'}
                        {overviewData.data_privacy_role === 'processor' && 'You process personal information on behalf of a responsible party'}
                        {overviewData.data_privacy_role === 'neither' && 'No POPIA data processing obligations apply'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cross-Border Transfers */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Globe} title="Cross-Border Data Transfers" />
                <div className={`p-4 rounded-lg ${
                  overviewData.has_cross_border_transfers 
                    ? 'bg-amber-500/10 border border-amber-500/30' 
                    : 'bg-emerald-500/10 border border-emerald-500/30'
                }`}>
                  <div className="flex items-center gap-2">
                    {overviewData.has_cross_border_transfers ? (
                      <>
                        <Globe size={20} className="text-amber-400" />
                        <span className="text-sm text-amber-400 font-medium">Cross-border transfers occur</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={20} className="text-emerald-400" weight="fill" />
                        <span className="text-sm text-emerald-400 font-medium">No cross-border transfers</span>
                      </>
                    )}
                  </div>
                </div>
                
                {overviewData.has_cross_border_transfers && overviewData.cross_border_countries?.length > 0 && (
                  <div className="mt-4">
                    <label className="text-xs text-text-tertiary uppercase tracking-wider mb-2 block">Transfer Countries</label>
                    <div className="flex flex-wrap gap-2">
                      {overviewData.cross_border_countries.map((country, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-amber-500/10 text-amber-400 text-sm rounded-lg flex items-center gap-1">
                          <MapPin size={14} />
                          {country}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Beneficial Ownership Tab */}
          {overviewData?.has_v2_intake && activeTab === 'ownership' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* NAVI Analysis Status */}
              {overviewData.navi_analysis_status && (
                <div className={`p-4 rounded-lg border ${
                  overviewData.navi_analysis_status === 'completed' && overviewData.navi_analysis_approved 
                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                    : overviewData.navi_analysis_status === 'pending'
                      ? 'bg-amber-500/10 border-amber-500/30'
                      : 'bg-background-surface border-border'
                }`}>
                  <div className="flex items-center gap-2">
                    <Robot size={20} className={
                      overviewData.navi_analysis_status === 'completed' && overviewData.navi_analysis_approved 
                        ? 'text-emerald-400' 
                        : 'text-amber-400'
                    } />
                    <span className="text-sm font-medium text-text-primary">
                      NAVI Document Analysis: {overviewData.navi_analysis_status}
                      {overviewData.navi_analysis_approved !== null && (
                        <span className={overviewData.navi_analysis_approved ? ' - Approved' : ' - Rejected'}></span>
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Beneficial Owners */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={Users} title={`Beneficial Owners (${overviewData.beneficial_owners?.length || 0})`} />
                {overviewData.beneficial_owners?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {overviewData.beneficial_owners.map((owner, idx) => (
                      <BeneficialOwnerCard key={idx} owner={owner} />
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No beneficial owners specified</p>
                )}
              </div>

              {/* Executive Directors */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={User} title={`Executive Directors (${overviewData.executive_directors?.length || 0})`} />
                {overviewData.executive_directors?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {overviewData.executive_directors.map((director, idx) => (
                      <DirectorCard key={idx} director={director} type="executive" />
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No executive directors specified</p>
                )}
              </div>

              {/* Non-Executive Directors */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <SectionHeader icon={User} title={`Non-Executive Directors (${overviewData.non_executive_directors?.length || 0})`} />
                {overviewData.non_executive_directors?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {overviewData.non_executive_directors.map((director, idx) => (
                      <DirectorCard key={idx} director={director} type="non-executive" />
                    ))}
                  </div>
                ) : (
                  <p className="text-text-tertiary text-sm italic">No non-executive directors specified</p>
                )}
              </div>
            </motion.div>
          )}

          {/* Methodology Tab */}
          {activeTab === 'methodology' && (
            <motion.div 
              key="methodology"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Current Methodology Status */}
              <div className={`p-4 rounded-lg border ${
                usingMethodology === 'custom' 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : 'bg-gold/10 border-gold/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ChartPie size={24} className={usingMethodology === 'custom' ? 'text-emerald-400' : 'text-gold'} />
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        Currently Using: {usingMethodology === 'custom' ? 'Custom Methodology' : 'AURA Default Methodology'}
                      </p>
                      <p className="text-xs text-text-tertiary">
                        {usingMethodology === 'custom' 
                          ? 'Your organization\'s custom risk rating framework is active' 
                          : 'Using AURA\'s standard 5x5 risk matrix for regulatory assessments'}
                      </p>
                    </div>
                  </div>
                  {usingMethodology === 'aura_default' && (
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="px-4 py-2 bg-gold text-background rounded-lg text-sm font-medium hover:bg-gold/90 transition-colors flex items-center gap-2"
                    >
                      <Upload size={16} />
                      Upload Your Methodology
                    </button>
                  )}
                </div>
              </div>

              {/* AURA Default Methodology Card */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
                      <Shield size={20} className="text-gold" />
                      AURA Default Methodology
                    </h3>
                    <p className="text-sm text-text-tertiary mt-1">Standard 5x5 risk matrix for regulatory compliance</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    usingMethodology === 'aura_default' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-background border border-border text-text-tertiary'
                  }`}>
                    {usingMethodology === 'aura_default' ? 'Active' : 'Available'}
                  </span>
                </div>

                {auraDefault && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Impact Scale */}
                    <div className="bg-background rounded-lg p-4 border border-border">
                      <p className="text-sm font-medium text-text-primary mb-2">Impact Scale</p>
                      <div className="space-y-1">
                        {auraDefault.impact_scale?.map((level, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <span className="w-5 h-5 rounded bg-gold/20 text-gold flex items-center justify-center font-mono">{level.level}</span>
                            <span className="text-text-secondary">{level.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Probability Scale */}
                    <div className="bg-background rounded-lg p-4 border border-border">
                      <p className="text-sm font-medium text-text-primary mb-2">Probability Scale</p>
                      <div className="space-y-1">
                        {auraDefault.probability_scale?.map((level, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <span className="w-5 h-5 rounded bg-gold/20 text-gold flex items-center justify-center font-mono">{level.level}</span>
                            <span className="text-text-secondary">{level.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Risk Levels */}
                    <div className="col-span-2 bg-background rounded-lg p-4 border border-border">
                      <p className="text-sm font-medium text-text-primary mb-2">Risk Levels</p>
                      <div className="flex gap-3">
                        {auraDefault.risk_levels?.map((level, idx) => (
                          <div key={idx} className="flex-1 p-2 rounded-lg border border-border text-center">
                            <div 
                              className="w-4 h-4 rounded-full mx-auto mb-1" 
                              style={{ backgroundColor: level.color }}
                            />
                            <p className="text-xs font-medium text-text-primary">{level.label}</p>
                            <p className="text-xs text-text-tertiary">{level.score_range[0]}-{level.score_range[1]}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Methodologies Section */}
              <div className="bg-background-surface border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
                    <FileText size={20} className="text-gold" />
                    Your Custom Methodologies
                  </h3>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="px-3 py-1.5 bg-gold/10 text-gold border border-gold/30 rounded-lg text-sm font-medium hover:bg-gold/20 transition-colors flex items-center gap-2"
                  >
                    <Plus size={14} />
                    Add Methodology
                  </button>
                </div>

                {methodologyLoading ? (
                  <div className="text-center py-8">
                    <div className="text-gold font-mono text-sm">Loading methodologies...</div>
                  </div>
                ) : methodologies.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-border rounded-lg">
                    <CloudArrowUp size={40} className="mx-auto text-text-tertiary mb-2" />
                    <p className="text-text-tertiary text-sm">No custom methodologies uploaded</p>
                    <p className="text-text-tertiary text-xs mt-1">Upload your risk rating framework to customize how AURA calculates risk scores</p>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="mt-4 px-4 py-2 bg-gold text-background rounded-lg text-sm font-medium hover:bg-gold/90 transition-colors"
                    >
                      Upload Methodology
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {methodologies.map((methodology) => (
                      <div 
                        key={methodology.id} 
                        className={`p-4 rounded-lg border ${
                          methodology.is_active 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : 'bg-background border-border'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <FileText size={18} className={methodology.is_active ? 'text-emerald-400' : 'text-text-secondary'} />
                              <h4 className="font-medium text-text-primary">{methodology.name}</h4>
                              {methodology.is_active && (
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded font-medium">
                                  Active
                                </span>
                              )}
                              <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                methodology.extraction_status === 'completed' 
                                  ? 'bg-emerald-500/20 text-emerald-400' 
                                  : methodology.extraction_status === 'pending'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : 'bg-red-500/20 text-red-400'
                              }`}>
                                {methodology.extraction_status === 'completed' ? 'Parsed' : 
                                 methodology.extraction_status === 'pending' ? 'Processing...' : 'Failed'}
                              </span>
                            </div>
                            <p className="text-xs text-text-tertiary mt-1">
                              {methodology.file_name} • Uploaded {new Date(methodology.uploaded_at).toLocaleDateString()}
                            </p>
                            {methodology.description && (
                              <p className="text-sm text-text-secondary mt-2">{methodology.description}</p>
                            )}

                            {/* Extracted Data Preview */}
                            {methodology.extraction_status === 'completed' && methodology.extracted_data && (
                              <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                                <p className="text-xs font-medium text-text-secondary mb-2">Extracted Framework:</p>
                                <div className="flex gap-4 text-xs">
                                  <div>
                                    <span className="text-text-tertiary">Matrix:</span>{' '}
                                    <span className="text-text-primary">{methodology.extracted_data.risk_matrix?.size || '5x5'}</span>
                                  </div>
                                  <div>
                                    <span className="text-text-tertiary">Impact Levels:</span>{' '}
                                    <span className="text-text-primary">{methodology.extracted_data.impact_scale?.length || 5}</span>
                                  </div>
                                  <div>
                                    <span className="text-text-tertiary">Risk Levels:</span>{' '}
                                    <span className="text-text-primary">{methodology.extracted_data.risk_levels?.length || 4}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 ml-4">
                            {methodology.extraction_status === 'failed' && (
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/company-profile/methodologies/${methodology.id}/retry-extraction`);
                                    toast.success('Extraction retry started');
                                    fetchMethodologies();
                                  } catch (error) {
                                    toast.error('Failed to retry extraction');
                                  }
                                }}
                                className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                                title="Retry extraction"
                              >
                                <Lightning size={16} />
                              </button>
                            )}
                            {!methodology.is_active && methodology.extraction_status === 'completed' && (
                              <button
                                onClick={() => {
                                  setSelectedMethodology(methodology);
                                  setShowActivateModal(true);
                                }}
                                className="px-3 py-1.5 bg-gold/10 text-gold border border-gold/30 rounded-lg text-xs font-medium hover:bg-gold/20 transition-colors"
                              >
                                Activate
                              </button>
                            )}
                            {!methodology.is_active && (
                              <button
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this methodology?')) {
                                    try {
                                      await axios.delete(`${API}/company-profile/methodologies/${methodology.id}`);
                                      toast.success('Methodology deleted');
                                      fetchMethodologies();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Failed to delete');
                                    }
                                  }
                                }}
                                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Delete methodology"
                              >
                                <Trash size={16} />
                              </button>
                            )}
                            <a
                              href={methodology.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-text-secondary hover:text-gold hover:bg-gold/10 rounded-lg transition-colors"
                              title="View document"
                            >
                              <Eye size={16} />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Upload Methodology Modal */}
          <AnimatePresence>
            {showUploadModal && (
              <UploadMethodologyModal 
                onClose={() => setShowUploadModal(false)}
                onSuccess={() => {
                  setShowUploadModal(false);
                  fetchMethodologies();
                }}
              />
            )}
          </AnimatePresence>

          {/* Activate Methodology Modal */}
          <AnimatePresence>
            {showActivateModal && selectedMethodology && (
              <ActivateMethodologyModal
                methodology={selectedMethodology}
                onClose={() => {
                  setShowActivateModal(false);
                  setSelectedMethodology(null);
                }}
                onSuccess={() => {
                  setShowActivateModal(false);
                  setSelectedMethodology(null);
                  fetchMethodologies();
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// Upload Methodology Modal Component
const UploadMethodologyModal = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'general', label: 'General Risk Framework' },
    { id: 'aml_cft', label: 'AML/CFT Methodology' },
    { id: 'operational', label: 'Operational Risk' },
    { id: 'compliance', label: 'Compliance Risk' },
    { id: 'it_cyber', label: 'IT/Cyber Risk' },
    { id: 'conduct', label: 'Conduct Risk' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !file) {
      toast.error('Please provide a name and upload a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      formData.append('category', category);
      formData.append('description', description);

      await axios.post(`${API}/company-profile/methodologies/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Methodology uploaded! AI is extracting the framework...');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload methodology');
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-background-surface border border-border rounded-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-medium text-text-primary">Upload Risk Methodology</h2>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-text-tertiary mt-1">
            Upload your organization's risk rating methodology document (PDF or Word)
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Methodology Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Enterprise Risk Assessment Framework 2024"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the methodology..."
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Document *</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => setFile(e.target.files[0])}
              accept=".pdf,.doc,.docx"
              className="hidden"
            />
            <div 
              onClick={() => fileInputRef.current?.click()}
              className={`w-full p-4 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                file ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-border hover:border-gold/50 hover:bg-gold/5'
              }`}
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText size={20} className="text-emerald-400" />
                  <span className="text-text-primary">{file.name}</span>
                  <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    className="text-text-tertiary hover:text-red-400"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div>
                  <CloudArrowUp size={32} className="mx-auto text-text-tertiary mb-2" />
                  <p className="text-sm text-text-secondary">Click to upload or drag and drop</p>
                  <p className="text-xs text-text-tertiary mt-1">PDF or Word documents only</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !name || !file}
              className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload & Analyze
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Activate Methodology Modal with Confirmation
const ActivateMethodologyModal = ({ methodology, onClose, onSuccess }) => {
  const [rationale, setRationale] = useState('');
  const [triggerRecalculation, setTriggerRecalculation] = useState(true);
  const [activating, setActivating] = useState(false);

  const handleActivate = async () => {
    if (!rationale.trim()) {
      toast.error('Please provide a rationale for this change');
      return;
    }

    setActivating(true);
    try {
      await axios.post(`${API}/company-profile/methodologies/${methodology.id}/activate`, {
        confirm: true,
        rationale: rationale.trim(),
        trigger_recalculation: triggerRecalculation
      });

      toast.success(
        triggerRecalculation 
          ? 'Methodology activated! Risk ratings are being recalculated...' 
          : 'Methodology activated successfully'
      );
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate methodology');
    } finally {
      setActivating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-background-surface border border-border rounded-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-amber-500/30 bg-amber-500/10">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Warning size={24} className="text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-medium text-text-primary">Activate Methodology</h2>
              <p className="text-sm text-text-secondary mt-1">
                You are about to activate <strong className="text-text-primary">{methodology.name}</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-sm text-amber-300">
              <strong>Important:</strong> Activating this methodology will change how AURA calculates risk ratings 
              for your Regulatory Universe. This may affect existing risk scores and compliance assessments.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={triggerRecalculation}
                onChange={(e) => setTriggerRecalculation(e.target.checked)}
                className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold"
              />
              <span className="text-sm text-text-primary">
                Recalculate existing risk ratings using this methodology
              </span>
            </label>
            <p className="text-xs text-text-tertiary ml-7 mt-1">
              If checked, all existing regulations in your Regulatory Universe will be recalculated
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Rationale for this change *
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain why you are activating this methodology..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold resize-none"
              required
            />
            <p className="text-xs text-text-tertiary mt-1">
              This will be logged in the audit trail for compliance purposes
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleActivate}
              disabled={activating || !rationale.trim()}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {activating ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Activating...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Yes, Activate Methodology
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CompanyOverviewPage;
